import React from "react";
import { Controller } from "react-hook-form";
import CreatableSelect from "react-select/creatable";

function StudentElementsUI({
  fields,
  control,
  register,
  errors,
  watch,
  elementsData,
  append,
  remove,
  handleAttachmentFileChange,
  fileCounts,
  fileUrls,
  setValue,
  getValues,
  trigger,
}) {
  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <div className="elements-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0 fw-bold header-title">Manage Student Elements</h5>
        <button
          className="btn btn-add-element d-flex align-items-center gap-2"
          type="button"
          onClick={() =>
            append({
              id: 0,
              name: "",
              description: "",
              createdBy: localStorage.getItem("userId"),
              elementDetail: {
                value: "",
                description: "",
                effectiveDate: null,
                endDate: null,
                createdBy: localStorage.getItem("userId"),
                fileName: "",
                filePath: "",
                base64String: "",
              },
            })
          }
        >
          <i className="fas fa-plus"></i> Add New Element
        </button>
      </div>

      {fields.map((field, index) => (
        <div className="element-card mb-4" key={field.id}>
          <div className="element-card-header d-flex justify-content-between align-items-center">
            <span className="element-number">Element #{index + 1}</span>
            {fields.length > 1 && (
              <button
                className="btn-remove-element"
                type="button"
                onClick={() => remove(index)}
                title="Remove Element"
              >
                <i className="fas fa-trash-alt me-1"></i> Remove
              </button>
            )}
          </div>
          <div className="element-card-body p-4">
            <div className="row g-4">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label mini-label">Element Name</label>
                  <Controller
                    name={`elements[${index}].name`}
                    control={control}
                    rules={{
                      required: "Element name is required",
                    }}
                    render={({ field }) => (
                      <CreatableSelect
                        {...field}
                        value={field.value || null}
                        options={elementsData.map((element) => ({
                          value: element.id,
                          label: element.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "create-option") {
                            setValue(`elements[${index}].name`, {
                              value: 0,
                              label: selectedOption.label,
                            });
                          } else if (actionMeta.action === "clear") {
                            setValue(`elements[${index}].name`, null);
                          } else {
                            setValue(
                              `elements[${index}].name`,
                              selectedOption || null
                            );
                          }
                          trigger(`elements[${index}].name`);
                        }}
                        classNamePrefix="premium-select"
                        placeholder="Select or Create..."
                        isClearable
                      />
                    )}
                  />
                  {errors.elements?.[index]?.name && (
                    <small className="error-text">
                      {errors.elements[index].name.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label mini-label">Value</label>
                  <input
                    {...register(`elements[${index}].elementDetail.value`, {
                      required: watch(`elements[${index}].name`)
                        ? "Value is required."
                        : false,
                    })}
                    type="text"
                    className={`form-control premium-input ${errors.elements?.[index]?.elementDetail?.value ? 'is-invalid' : ''}`}
                    placeholder="Enter value"
                  />
                  {errors.elements?.[index]?.elementDetail?.value && (
                    <small className="error-text">
                      {errors.elements[index].elementDetail.value.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label mini-label">Effective Date</label>
                  <input
                    {...register(`elements[${index}].elementDetail.effectiveDate`, {
                      validate: {
                        lessThanEndDate: (value) => {
                          const endDate = getValues(
                            `elements[${index}].elementDetail.endDate`
                          );
                          return (
                            !value || 
                            !endDate ||
                            new Date(value) <= new Date(endDate) ||
                            "Effective Date cannot be after End Date."
                          );
                        },
                      },
                    })}
                    type="date"
                    className="form-control premium-input"
                  />
                  {errors.elements?.[index]?.elementDetail?.effectiveDate && (
                    <small className="error-text">
                      {errors.elements[index].elementDetail.effectiveDate.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label mini-label">End Date</label>
                  <input
                    {...register(`elements[${index}].elementDetail.endDate`, {
                      validate: {
                        greaterThanEffectiveDate: (value) => {
                          const effectiveDate = getValues(
                            `elements[${index}].elementDetail.effectiveDate`
                          );
                          return (
                            !value || 
                            !effectiveDate ||
                            new Date(value) >= new Date(effectiveDate) ||
                            "End Date cannot be before Effective Date."
                          );
                        },
                      },
                    })}
                    type="date"
                    className="form-control premium-input"
                  />
                  {errors.elements?.[index]?.elementDetail?.endDate && (
                    <small className="error-text">
                      {errors.elements[index].elementDetail.endDate.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="col-md-8">
                <div className="form-group">
                  <label className="form-label mini-label">Description</label>
                  <textarea
                    {...register(`elements[${index}].elementDetail.description`, {
                      maxLength: {
                        value: 250,
                        message: "Maximum of 250 characters allowed.",
                      },
                    })}
                    className="form-control premium-textarea"
                    rows="2"
                    placeholder="Add specific details about this element..."
                    style={{ resize: "none" }}
                    onChange={handleChangeOfInputWithLengthCheck(
                      `elements[${index}].elementDetail.description`
                    )}
                  />
                  {errors.elements?.[index]?.elementDetail?.description && (
                    <small className="error-text">
                      {errors.elements[index].elementDetail.description.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group h-100 d-flex flex-column">
                  <label className="form-label mini-label">Attachment</label>
                  <div className="flex-grow-1 d-flex gap-2">
                    <div className="upload-wrapper flex-grow-1 position-relative">
                      <button
                        className="btn-premium-upload w-100"
                        type="button"
                      >
                        <i className="fas fa-cloud-upload-alt me-2"></i>
                        <span>{fileCounts[index] ? "Replace File" : "Choose File"}</span>
                        <input
                          type="file"
                          className="attachment-input"
                          onChange={(event) =>
                            handleAttachmentFileChange(event, index)
                          }
                        />
                      </button>
                    </div>
                    {fileCounts[index] ? (
                      <a
                        href={fileUrls[index]}
                        title="Preview Attachment"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-preview-attachment"
                      >
                        <i className="fa fa-eye"></i>
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      
      <style>{`
        .header-title {
          color: #2d3748;
          font-size: 1.15rem;
          letter-spacing: -0.01em;
        }

        .btn-add-element {
          background-color: #4a5568;
          color: white;
          border: none;
          padding: 0.5rem 1.25rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.85rem;
          transition: all 0.2s ease;
        }

        .btn-add-element:hover {
          background-color: #2d3748;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .element-card {
          background: #ffffff;
          border-radius: 16px;
          border: 1px solid #edf2f7;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
          overflow: hidden;
          transition: transform 0.2s ease;
        }

        .element-card-header {
          background: #f8fafc;
          padding: 0.75rem 1.5rem;
          border-bottom: 1px solid #edf2f7;
        }

        .element-number {
          font-weight: 700;
          color: #718096;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .btn-remove-element {
          background: transparent;
          border: none;
          color: #e53e3e;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          padding: 0;
          transition: color 0.2s ease;
        }

        .btn-remove-element:hover {
          color: #c53030;
        }

        .mini-label {
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          color: #a0aec0;
          margin-bottom: 0.5rem;
          letter-spacing: 0.06em;
          display: block;
        }

        .premium-input, .premium-textarea {
          border-color: #e2e8f0 !important;
          border-radius: 10px !important;
          padding: 0.6rem 1rem !important;
          font-size: 0.9rem !important;
          color: #2d3748 !important;
          outline: none !important;
          transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
        }

        .premium-input:focus, .premium-textarea:focus {
          border-color: #cbd5e0 !important;
          box-shadow: 0 0 0 3px rgba(226, 232, 240, 0.4) !important;
        }

        .premium-select__control {
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          min-height: 42px !important;
          box-shadow: none !important;
        }

        .premium-select__placeholder {
          font-size: 0.9rem !important;
          color: #a0aec0 !important;
        }

        .premium-select__single-value {
          font-size: 0.9rem !important;
          color: #2d3748 !important;
        }

        .btn-premium-upload {
          background: #f7fafc;
          border: 1.5px dashed #cbd5e0;
          color: #4a5568;
          border-radius: 10px;
          padding: 0.6rem 1rem;
          font-size: 0.85rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          height: 42px;
        }

        .btn-premium-upload:hover {
          background: #edf2f7;
          border-color: #a0aec0;
        }

        .attachment-input {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }

        .btn-preview-attachment {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ebf8ff;
          color: #3182ce;
          border-radius: 10px;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .btn-preview-attachment:hover {
          background: #bee3f8;
          color: #2b6cb0;
        }

        .error-text {
          color: #e53e3e;
          font-size: 0.7rem;
          margin-top: 0.4rem;
          font-weight: 500;
          display: block;
        }
      `}</style>
    </div>
  );
}

export default StudentElementsUI;
